// src/components/financials/AssetManager.js
// Created: 01/14/2026

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, Button, 
  Table, TableBody, TableCell, TableHead, TableRow, 
  IconButton, Card, CardContent 
} from '@mui/material';
import { AddCircle, Delete, HomeWork, DirectionsBus, Computer } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const AssetManager = () => {
  const { userProfile } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newAsset, setNewAsset] = useState({ name: '', cost: '', type: 'Building', purchaseDate: '' });

  const churchId = userProfile?.churchId;

  useEffect(() => {
    if (churchId) fetchAssets();
  }, [churchId]);

  const fetchAssets = async () => {
    const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'assets'));
    setAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleAdd = async () => {
    if (!newAsset.name || !newAsset.cost) return;
    setLoading(true);
    try {
      await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'assets'), {
        ...newAsset,
        cost: parseFloat(newAsset.cost),
        createdAt: serverTimestamp()
      });
      setNewAsset({ name: '', cost: '', type: 'Building', purchaseDate: '' });
      fetchAssets();
    } catch (e) {
      alert(e.message);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this asset from records?")) return;
    await deleteDoc(doc(db, 'churches', churchId, 'financials', 'data', 'assets', id));
    fetchAssets();
  };

  const getIcon = (type) => {
    if (type === 'Vehicle') return <DirectionsBus sx={{ mr: 1, color: 'primary.main' }} />;
    if (type === 'Equipment') return <Computer sx={{ mr: 1, color: 'primary.main' }} />;
    return <HomeWork sx={{ mr: 1, color: 'primary.main' }} />;
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeWork sx={{ mr: 1 }} /> Fixed Assets (Property & Equipment)
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Track the value of land, buildings, vehicles, and high-value equipment.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Asset Name" size="small" value={newAsset.name} onChange={(e) => setNewAsset({...newAsset, name: e.target.value})} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth label="Type" size="small" SelectProps={{ native: true }} value={newAsset.type} onChange={(e) => setNewAsset({...newAsset, type: e.target.value})}>
              <option value="Building">Building/Land</option>
              <option value="Vehicle">Vehicle</option>
              <option value="Equipment">Equipment</option>
              <option value="Other">Other</option>
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth label="Cost/Value" size="small" type="number" value={newAsset.cost} onChange={(e) => setNewAsset({...newAsset, cost: e.target.value})} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="date" label="Purchase Date" size="small" InputLabelProps={{shrink:true}} value={newAsset.purchaseDate} onChange={(e) => setNewAsset({...newAsset, purchaseDate: e.target.value})} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" startIcon={<AddCircle />} onClick={handleAdd} disabled={loading}>Add Asset</Button>
          </Grid>
        </Grid>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Asset</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((a) => (
              <TableRow key={a.id}>
                <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                    {getIcon(a.type)} {a.name}
                </TableCell>
                <TableCell>{a.type}</TableCell>
                <TableCell>{a.purchaseDate}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${(a.cost || 0).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default AssetManager;